import { Checked, EmoTarget, EmoTrophy } from "@assets"
import { router } from "@inertiajs/react"

import { Dialog, DialogContent } from "@/components/ui/dialog"
import { useAuth } from "@/hooks/use-auth"
import { feedPath, userPath } from "@/routes"

interface Challenge {
  key: string
  title: string
  points: number
  icon: string
  completed: boolean
}

interface WeeklyChallengeModalProps {
  isOpen: boolean
  onClose: () => void
  challenges: Challenge[]
  allCompleted: boolean
  totalPossiblePoints: number
  userTotalPoints?: number
}

export function WeeklyChallengeModal({
  isOpen,
  onClose,
  challenges,
  allCompleted,
  totalPossiblePoints,
  userTotalPoints = 0,
}: WeeklyChallengeModalProps) {
  const { user } = useAuth()
  const completedCount = challenges.filter((c) => c.completed).length
  const totalCount = challenges.length
  const earnedPoints = challenges
    .filter((c) => c.completed)
    .reduce((sum, c) => sum + c.points, 0)

  const handleChallengeClick = (challengeKey: string) => {
    let filter = null
    switch (challengeKey) {
      case "attend_event":
        filter = "eventos"
        break
      case "answer_question":
        filter = "foro"
        break
      case "write_review":
        filter = "mercado"
        break
      case "share_with_friend":
        if (user) {
          router.visit(`${userPath(user.id)}?scrollToSharing=true`)
        }
        return
      default:
        filter = "todos"
    }

    router.get(
      feedPath(),
      {
        filter: filter,
        scrollToFilters: "true",
      },
      {
        preserveState: false,
        preserveScroll: false,
      },
    )
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        showWelcomeLogo={false}
        className="bg-card mx-auto max-w-[320px] rounded-[24px] border-0 p-0 shadow-xl"
      >
        <div className="px-6 pt-2 pb-6">
          <div className="my-[13px] flex justify-center">
            <div className="gradient-avatar flex h-[80px] w-[80px] items-center justify-center rounded-full">
              <img
                src={EmoTarget}
                alt="Challenge Icon"
                className="h-full w-auto"
              />
            </div>
          </div>

          <h2 className="text-chart-5 mb-[20px] h-[36px] text-center text-[21px] leading-[28px] font-semibold tracking-[0px]">
            Desafíos de la Semana
          </h2>

          <div className="mb-[25px]">
            {challenges.map((challenge) => (
              <div
                key={challenge.key}
                className={`border-chart-4 flex h-[44px] cursor-pointer items-center gap-[12px] border-b-[2px] pr-[9px] pl-[13px] transition-colors hover:bg-gray-50 ${
                  challenge.completed ? "bg-sidebar-primary" : "bg-card"
                }`}
                onClick={() => handleChallengeClick(challenge.key)}
              >
                <div
                  className={`flex h-[20px] w-[20px] items-center justify-center rounded-[4px] border-[2px] ${
                    challenge.completed
                      ? "bg-chart-3 border-chart-3"
                      : "border-chart-2 bg-card"
                  }`}
                >
                  {challenge.completed && (
                    <Checked className="h-4 w-4 fill-white" />
                  )}
                </div>
                <span
                  className={`text-chart-5 flex h-[19px] flex-1 items-center text-[13px] leading-[30px] font-semibold tracking-[0.07px] ${
                    challenge.completed ? "line-through" : ""
                  }`}
                >
                  {challenge.title}
                </span>
                <span className="text-chart-5 flex h-[19px] items-center text-[13px] leading-[30px] font-normal tracking-[0px]">
                  +{challenge.points}
                </span>
              </div>
            ))}
          </div>

          <div className="bg-card flex items-center gap-[9px] pb-[7px]">
            <div className="bg-modal h-[8px] flex-1 overflow-hidden rounded-full">
              <div
                className={`h-full rounded-[204px] transition-all ${completedCount === totalCount ? "bg-active" : "bg-star"}`}
                style={{ width: `${(completedCount / totalCount) * 100}%` }}
              />
            </div>

            <span
              className={`h-[39px] w-[49px] flex-shrink-0 text-center text-[24px] leading-[35px] font-bold tracking-[0px] ${completedCount === totalCount ? "text-active" : "text-star"}`}
            >
              {completedCount}/{totalCount}
            </span>
          </div>

          <div className="text-center">
            {allCompleted ? (
              <>
                <div className="mb-[6px] flex items-center justify-center gap-[10px]">
                  <div className="bg-modal flex h-[44px] w-[44px] items-center justify-center rounded-full">
                    <img
                      src={EmoTrophy}
                      alt="Trophy"
                      className="h-full w-full"
                    />
                  </div>
                  <span className="text-muted-foreground text-[14px] leading-[21px] font-medium tracking-[0.28px]">
                    Semana Perfecta!
                  </span>
                </div>
                <span className="text-chart-5 text-[18px] leading-[27px] font-semibold tracking-[0px]">
                  +{totalPossiblePoints} Puntos
                </span>
              </>
            ) : (
              <div className="text-chart-5 mb-2 h-[25px] text-[18px] leading-[27px] font-semibold tracking-[0px]">
                {earnedPoints} de {totalPossiblePoints} Puntos
              </div>
            )}

            <div className="text-muted-foreground h-[19px] text-[13px] leading-[20px] font-normal tracking-[0.2px]">
              Tus Puntos Bonus Totales:{" "}
              <span className="text-chart-5 font-medium">
                {userTotalPoints}
              </span>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
